import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Typography, Paper, Button, Collapse, Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import LoadingSpinner from "@/components/LoadingSpinner";
import CompanyDetailHeader from "@/components/company/CompanyDetailHeader";
import CompanyAboutSidebar from "@/components/company/CompanyAboutSidebar";
import AverageRatingsPanel from "@/components/ratings/AverageRatingsPanel";
import ReviewCard from "@/components/ReviewCard";
import ReviewForm from "@/components/ReviewForm";
import { useCompanyDetailPage } from "@/hooks";

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const {
    company,
    loading,
    error,
    reviewsData,
    showForm,
    setShowForm,
    submitting,
    hasReviewed,
    handleSubmitReview,
    isAuthenticated,
  } = useCompanyDetailPage(id);

  if (loading) {
    return <LoadingSpinner py={10} />;
  }

  if (error || !company) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error">{error || t("company.notFound")}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <CompanyDetailHeader company={company} />
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          {company.averageRatings && (
            <Paper sx={{ p: 4, mb: 4, borderRadius: 3 }}>
              <AverageRatingsPanel averageRatings={company.averageRatings} />
            </Paper>
          )}

          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                }}
              >
                {t("company.reviews")} ({reviewsData?.total ?? 0})
              </Typography>
              {isAuthenticated && !hasReviewed && (
                <Button
                  variant="contained"
                  onClick={() => setShowForm(!showForm)}
                  endIcon={showForm ? <ExpandLess /> : <ExpandMore />}
                >
                  {t("company.writeReview")}
                </Button>
              )}
            </Box>

            <Collapse in={showForm}>
              <Box
                sx={{ mb: 4, p: 2, border: 1, borderColor: "divider", borderRadius: 2 }}
              >
                <ReviewForm
                  submitLabel={t("review.submitReview")}
                  onSubmit={handleSubmitReview}
                  loading={submitting}
                />
              </Box>
            </Collapse>

            {reviewsData?.items.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}

            {reviewsData?.items.length === 0 && (
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  py: 4,
                }}
              >
                {t("company.noReviews")}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <CompanyAboutSidebar company={company} />
        </Grid>
      </Grid>
    </Container>
  );
}
